import { useRef } from 'react';

// Utils & misc
import cn from '../../../utils/cn';

// Components
import Picto from '../../atoms/Picto/Picto';

// Style
import style from './SearchField.module.css';

/**
 * SearchField component - A navigation search with icon, label and states
 * @param {object} params
 * @param {string} params.id - Input id
 * @param {string} params.placeholder - Input placeholder
 * @param {string} [params.className] - Additional CSS classes
 * @param {() => void} params.onFocus - Focus handler
 * @param {boolean} [params.disabled=false] - Whether the input is disabled
 * @param {string} [params.value] - Input value
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} [params.onChange] - Input change handler
 * @returns {JSX.Element} SearchField component
 */
function SearchField({
  id = 'search-field',
  placeholder,
  onFocus,
  className,
  disabled = false,
  value,
  onChange,
}) {
  const inputRef = useRef(null);

  const handleClearInput = () => {
    const event = /** @type {React.ChangeEvent<HTMLInputElement>} */ ({ target: { value: '' } });
    onChange?.(event);
    inputRef.current?.focus();
  };

  return (
    <label
      onFocus={onFocus}
      htmlFor={id}
      className={cn([
        style['search-field'],
        disabled && style.disabled,
        className,
      ])}
    >
      <input
        autoComplete="off"
        id={id}
        type="text"
        placeholder={placeholder}
        className={cn([style.input])}
        ref={inputRef}
        disabled={disabled}
        value={value}
        onChange={onChange}
      />
      {value && (
        <button type="button" className={style.close} onClick={handleClearInput}>
          <Picto
            icon="close-md"
            className={style['icon-close']}
          />
        </button>
      )}
      {!value && (
        <Picto icon="search-normal" className={style.icon} />
      )}
    </label>
  );
}

export default SearchField;
